import React, { useCallback, useEffect, useRef, useState } from "react";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import {
  CHAT_SOCKET_STATUS,
  chatComposerTextState,
  chatEditingMessageIdState,
  chatMessagesByRoomState,
  chatMessagesRefreshState,
  chatSocketConnectedState,
  selectedChatRoomState,
} from "../../state/chatState";
import { buildSentMessage, upsertMessageInRoomMap } from "../../chatMessageUtils";
import socketService from "../../../../socket/socketService";
import { SOCKET_APP_DESTINATIONS } from "../../../../socket/socketConfig";
import { SOCKET_EVENT_TYPES } from "../../../../socket/socketEventHandlers";
import { useReadMemory } from "../../../generic/hooks/useReadMemory";
import MemoryType from "../../../../service/memory/enum/MemoryType";
import { useGetOrgIdentifier } from "../../../generic/hooks/useGetOrgIdentifier";
import apiService from "../../../../service/apiservice/apiService";
import { getChatRoomId } from "../../chatRoomUtils";
import PrimaryButton from "../../../../components/generic/buttons/PrimaryButton";
import ChatComposerFormatToolbar from "./ChatComposerFormatToolbar";
import {
  applyWrapperToSelection,
  insertLinkAtSelection,
  isSafeChatLinkHref,
  normalizeChatLinkUrl,
} from "../../chatMarkdownUtils";

const buildMessagePayload = ({
  trimmedMessage,
  senderId,
  organizationIdentifier,
  roomId,
  messageId,
}) => ({
  ...(messageId ? { id: messageId } : {}),
  sender: senderId,
  senderType: "workforce",
  message: JSON.stringify({
    type: "text",
    message: "",
    chatMessage: trimmedMessage,
    actions: [],
  }),
  metadata: messageId ? JSON.stringify({ edited: true }) : "",
  organizationIdentifier,
  keywords: [{ keyword: `room_${roomId}`, metadata: "" }],
});

const TYPING_PUBLISH_INTERVAL_MS = 2000;

const ChatComposer = () => {
  const user = useReadMemory(MemoryType.USER);
  const organizationIdentifier = useGetOrgIdentifier();
  const [messageText, setMessageText] = useRecoilState(chatComposerTextState);
  const [editingMessageId, setEditingMessageId] = useRecoilState(
    chatEditingMessageIdState,
  );
  const selectedRoom = useRecoilValue(selectedChatRoomState);
  const socketStatus = useRecoilValue(chatSocketConnectedState);
  const setMessagesRefresh = useSetRecoilState(chatMessagesRefreshState);
  const setMessagesByRoom = useSetRecoilState(chatMessagesByRoomState);
  const [isSending, setIsSending] = useState(false);
  const lastTypingSentAtRef = useRef(0);
  const textareaRef = useRef(null);

  const roomId = selectedRoom ? getChatRoomId(selectedRoom) : null;
  const isSocketConnected = socketStatus === CHAT_SOCKET_STATUS.CONNECTED;

  const publishTyping = useCallback(() => {
    if (!roomId || !user?.userIdentifier || !socketService.isConnected()) {
      return;
    }

    const now = Date.now();
    if (now - lastTypingSentAtRef.current < TYPING_PUBLISH_INTERVAL_MS) {
      return;
    }

    lastTypingSentAtRef.current = now;
    socketService.publish(SOCKET_APP_DESTINATIONS.typing(roomId), {
      type: SOCKET_EVENT_TYPES.TYPING,
      roomId,
      userIdentifier: user.userIdentifier,
      userName:
        [user.givenName, user.familyName].filter(Boolean).join(" ") ||
        user.userIdentifier,
    });
  }, [roomId, user?.familyName, user?.givenName, user?.userIdentifier]);

  useEffect(() => {
    lastTypingSentAtRef.current = 0;
  }, [roomId]);

  const isEditing = Boolean(editingMessageId);

  const handleCancelEdit = useCallback(() => {
    setEditingMessageId(null);
    setMessageText("");
  }, [setEditingMessageId, setMessageText]);

  const canSend =
    Boolean(roomId) &&
    Boolean(user?.userIdentifier) &&
    Boolean(organizationIdentifier) &&
    Boolean(messageText.trim()) &&
    !isSending;

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();

      const trimmedMessage = messageText.trim();
      if (
        !trimmedMessage ||
        !roomId ||
        !user?.userIdentifier ||
        !organizationIdentifier ||
        isSending
      ) {
        return;
      }

      setIsSending(true);
      try {
        const payload = buildMessagePayload({
          trimmedMessage,
          senderId: user.userIdentifier,
          organizationIdentifier,
          roomId,
          messageId: editingMessageId,
        });

        const response = await apiService.post(
          "niramayafoundation/api/v1/communication/message",
          payload,
          null,
          isEditing ? "Failed to update message" : "Failed to send message",
        );

        if (response?.id) {
          const sentMessage = buildSentMessage({
            response,
            trimmedMessage,
            sender: user,
            roomMembers: selectedRoom?.members ?? [],
            isEdited: isEditing,
          });

          if (sentMessage) {
            setMessagesByRoom((prev) =>
              upsertMessageInRoomMap(prev, roomId, sentMessage),
            );
          }

          setMessageText("");
          setEditingMessageId(null);
          if (!isSocketConnected || isEditing) {
            setMessagesRefresh((version) => version + 1);
          }
        }
      } finally {
        setIsSending(false);
      }
    },
    [editingMessageId, isEditing, isSending, messageText, organizationIdentifier, roomId, setEditingMessageId, setMessageText, isSocketConnected, setMessagesRefresh, setMessagesByRoom, selectedRoom?.members, user],
  );

  const handleInputChange = useCallback(
    (event) => {
      const nextValue = event.target.value;
      setMessageText(nextValue);
      if (nextValue.trim()) {
        publishTyping();
      }
    },
    [publishTyping, setMessageText],
  );

  const applyTextUpdate = useCallback(
    (updater) => {
      const textarea = textareaRef.current;
      if (!textarea) {
        return;
      }

      const { value, selectionStart, selectionEnd } = textarea;
      const result = updater(value, selectionStart, selectionEnd);
      setMessageText(result.nextValue);

      requestAnimationFrame(() => {
        textarea.focus();
        textarea.setSelectionRange(
          result.selectionStart,
          result.selectionEnd,
        );
      });

      if (result.nextValue.trim()) {
        publishTyping();
      }
    },
    [publishTyping, setMessageText],
  );

  const handleFormat = useCallback(
    (formatId) => {
      if (!selectedRoom || isSending) {
        return;
      }

      if (formatId === "link") {
        const textarea = textareaRef.current;
        if (!textarea) {
          return;
        }

        const { value, selectionStart, selectionEnd } = textarea;
        const selected = value.slice(selectionStart, selectionEnd);
        const urlInput = window.prompt("Enter link URL");
        if (!urlInput?.trim()) {
          return;
        }

        const normalizedUrl = normalizeChatLinkUrl(urlInput);
        if (!isSafeChatLinkHref(normalizedUrl)) {
          window.alert("Please enter a valid http or https URL.");
          return;
        }

        let label = selected.trim();
        if (!label) {
          const labelInput = window.prompt("Enter link text", normalizedUrl);
          if (!labelInput?.trim()) {
            return;
          }
          label = labelInput.trim();
        }

        applyTextUpdate((currentValue, start, end) =>
          insertLinkAtSelection(
            currentValue,
            start,
            end,
            label,
            normalizedUrl,
          ),
        );
        return;
      }

      const wrappers = {
        bold: { before: "**", after: "**" },
        italic: { before: "*", after: "*" },
        strike: { before: "~~", after: "~~" },
      };
      const wrapper = wrappers[formatId];
      if (!wrapper) {
        return;
      }

      applyTextUpdate((currentValue, start, end) =>
        applyWrapperToSelection(
          currentValue,
          start,
          end,
          wrapper.before,
          wrapper.after,
        ),
      );
    },
    [applyTextUpdate, isSending, selectedRoom],
  );

  const handleKeyDown = useCallback(
    (event) => {
      if ((event.ctrlKey || event.metaKey) && !event.shiftKey) {
        const key = event.key.toLowerCase();
        if (key === "b") {
          event.preventDefault();
          handleFormat("bold");
          return;
        }
        if (key === "i") {
          event.preventDefault();
          handleFormat("italic");
          return;
        }
        if (key === "k") {
          event.preventDefault();
          handleFormat("link");
          return;
        }
      }

      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        if (canSend) {
          handleSubmit(event);
        }
      }
    },
    [canSend, handleFormat, handleSubmit],
  );

  return (
    <div className="conversation-view-input-container">
      {isEditing ? (
        <div className="conversation-view-editing-banner">
          <span>Editing message</span>
          <button
            type="button"
            className="conversation-view-editing-cancel"
            onClick={handleCancelEdit}
            disabled={isSending}
          >
            Cancel
          </button>
        </div>
      ) : null}
      <form onSubmit={handleSubmit} className="conversation-view-input-form">
        <div className="conversation-view-composer">
          <ChatComposerFormatToolbar
            onFormat={handleFormat}
            disabled={!selectedRoom || isSending}
          />
          <div className="conversation-view-input-row">
            <div className="conversation-view-input-wrapper">
              <textarea
                ref={textareaRef}
                rows={3}
                value={messageText}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder={isEditing ? "Edit message…" : "Message…"}
                className="conversation-view-input"
                disabled={!selectedRoom || isSending}
                aria-label={isEditing ? "Edit message text" : "Message text"}
              />
            </div>
            <PrimaryButton
              htmlType="submit"
              loading={isSending}
              buttonText={isEditing ? "Save" : "Send"}
              handleOnClick={handleSubmit}
              buttonDisable={!canSend}
            />
          </div>
        </div>
      </form>
    </div>
  );
};

export default ChatComposer;
